import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Plus, X, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  fetchCatalogBrands,
  fetchLockedBrands,
  addLockedBrand,
  removeLockedBrand,
} from "../lib/api";
import { track } from "../lib/analytics";

/**
 * LockedBrandsCard — Settings card where users pin specific brands so
 * those brands always surface higher in product recs, above organic
 * brand-affinity learning.
 *
 * Power user mental model: "I always want my routines to lead with
 * Fenty + ILIA, no matter what the algorithm learns about my clicks."
 *
 * Behaviour:
 *   1. Lists current locked brands as removable chips
 *   2. Picker dropdown sourced from /api/catalog/brands (only real
 *      catalog brands — typos can't lock a phantom)
 *   3. Search filter so users can find a brand in the 33-entry list
 *   4. Server enforces the 8-brand cap; we mirror it client-side for
 *      friendlier error states
 *   5. Idempotent — re-adding a locked brand is a soft no-op success
 */
const LockedBrandsCard = () => {
  const { t } = useTranslation();
  const [locked, setLocked] = useState([]);
  const [maxBrands, setMaxBrands] = useState(8);
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null); // brand currently being removed
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([fetchLockedBrands(), fetchCatalogBrands()])
      .then(([data, brands]) => {
        if (!alive) return;
        setLocked(data?.brands || []);
        setMaxBrands(data?.max_brands || 8);
        setCatalogBrands(brands || []);
      })
      .catch(() => {
        // Silent — show empty state. Toast would be noisy on a Settings
        // page that the user came to for other reasons.
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const onAdd = async (brand) => {
    if (adding) return;
    setAdding(true);
    try {
      const data = await addLockedBrand(brand);
      setLocked(data.brands);
      setPickerOpen(false);
      setSearch("");
      track("locked_brand_added", { brand });
      toast.success(t("lockedBrands.toastAdded", { brand }));
    } catch (e) {
      const msg = e?.response?.data?.detail || t("common.tryAgain");
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (brand) => {
    if (removing) return;
    setRemoving(brand);
    try {
      const data = await removeLockedBrand(brand);
      setLocked(data.brands);
      track("locked_brand_removed", { brand });
      toast.success(t("lockedBrands.toastRemoved", { brand }));
    } catch {
      toast.error(t("common.tryAgain"));
    } finally {
      setRemoving(null);
    }
  };

  // Show only brands the user hasn't already locked, filtered by search
  const lockedSet = new Set(locked.map((b) => b.toLowerCase()));
  const addable = catalogBrands.filter(
    (b) =>
      !lockedSet.has(b.toLowerCase()) &&
      b.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const atLimit = locked.length >= maxBrands;

  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-6 sm:p-7 soft-shadow mb-5"
      data-testid="locked-brands-card"
    >
      <div className="flex items-start gap-3 mb-1">
        <div className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center shrink-0">
          <Lock className="h-5 w-5 text-[#9B6A6C]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-medium text-[#2C2123]">
            {t("lockedBrands.title")}
          </h3>
          <p className="text-sm text-[#5C4B51] leading-relaxed mt-1 max-w-xl">
            {t("lockedBrands.body")}
          </p>
        </div>
      </div>

      {/* Current locked brand chips */}
      <div className="mt-5">
        {loading ? (
          <div className="flex gap-2">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-9 w-28 rounded-full bg-[#F8EEEE] animate-pulse"
              />
            ))}
          </div>
        ) : locked.length === 0 ? (
          <p
            className="text-sm text-[#8A7D82] italic"
            data-testid="locked-brands-empty"
          >
            {t("lockedBrands.empty")}
          </p>
        ) : (
          <div
            className="flex flex-wrap gap-2"
            data-testid="locked-brands-chips"
          >
            {locked.map((brand) => (
              <span
                key={brand}
                className="inline-flex items-center gap-1.5 bg-[#F4DCD3] border border-[#E5C5C2] text-[#5D4050] rounded-full pl-3 pr-2 py-1.5 text-sm"
                data-testid={`locked-brand-chip-${brand}`}
              >
                <Lock className="h-3 w-3" />
                {brand}
                <button
                  type="button"
                  onClick={() => onRemove(brand)}
                  disabled={removing === brand}
                  className="ml-1 h-5 w-5 rounded-full hover:bg-white/60 flex items-center justify-center disabled:opacity-50 transition-colors"
                  aria-label={t("lockedBrands.removeAria", { brand })}
                  data-testid={`locked-brand-remove-${brand}`}
                >
                  {removing === brand ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Add picker */}
      <div className="mt-5">
        {pickerOpen ? (
          <div className="rounded-2xl border border-[#EADDD7] bg-white overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-[#EADDD7]">
              <Search className="h-4 w-4 text-[#8A7D82]" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("lockedBrands.searchPlaceholder")}
                className="flex-1 bg-transparent text-sm text-[#2C2123] placeholder:text-[#8A7D82] focus:outline-none"
                data-testid="locked-brands-search"
              />
              <button
                type="button"
                onClick={() => {
                  setPickerOpen(false);
                  setSearch("");
                }}
                className="text-xs text-[#8A7D82] hover:text-[#5D4050]"
                data-testid="locked-brands-picker-close"
              >
                {t("common.cancel")}
              </button>
            </div>
            <div className="max-h-56 overflow-y-auto">
              {addable.length === 0 ? (
                <p className="text-center text-sm text-[#8A7D82] py-6 px-4">
                  {search
                    ? t("lockedBrands.noMatches")
                    : t("lockedBrands.allLocked")}
                </p>
              ) : (
                <ul>
                  {addable.map((brand) => (
                    <li key={brand}>
                      <button
                        type="button"
                        onClick={() => onAdd(brand)}
                        disabled={adding}
                        className="w-full text-left px-4 py-2.5 text-sm text-[#2C2123] hover:bg-[#FAF5F1] flex items-center justify-between gap-2 disabled:opacity-50 transition-colors"
                        data-testid={`locked-brands-option-${brand}`}
                      >
                        <span>{brand}</span>
                        <Plus className="h-3.5 w-3.5 text-[#9B6A6C]" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={atLimit || loading}
            className="inline-flex items-center gap-1.5 text-sm text-[#9B6A6C] hover:text-[#7d5455] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
            data-testid="locked-brands-add-btn"
          >
            <Plus className="h-4 w-4" />
            {atLimit
              ? t("lockedBrands.atLimit", { max: maxBrands })
              : t("lockedBrands.addBrand")}
          </button>
        )}
      </div>

      <p className="mt-4 text-xs text-[#8A7D82] leading-relaxed">
        {t("lockedBrands.footer")}
      </p>
    </div>
  );
};

export default LockedBrandsCard;
